import { isUnauthorized, type Me } from "@clubcore/api";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useServices } from "./services";

export type AuthState =
  | { status: "loading" }
  | { status: "signedOut"; expired: boolean; /** Who was signed in, and whether they chose to sign out. */ previousUserId?: string; explicit?: boolean }
  | { status: "error"; error: unknown }
  | { status: "signedIn"; me: Me };

interface AuthValue {
  state: AuthState;
  signIn: (email: string, password: string) => Promise<void>;
  completeMicrosoft: (code: string) => Promise<{ me: Me; path: string }>;
  completeSiteKings: (code: string) => Promise<{ me: Me; path: string }>;
  signOut: () => Promise<void>;
  retry: () => void;
  /** Re-reads /auth/me in place (no loading screen), e.g. after the club's settings changed. */
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

function deviceLabel(): string {
  const platform = navigator.userAgent.includes("iPad") ? "iPad" : navigator.platform || "Browser";
  return `ClubCore web (${platform})`.slice(0, 200);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { api, tokenStore, clubSelection, sessionEvents, queryClient, license } = useServices();
  const [state, setState] = useState<AuthState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  const generation = useRef(0);
  const currentUser = useRef<string | undefined>(undefined);

  const finishSignedOut = useCallback(
    (expired: boolean, explicit = false) => {
      generation.current += 1;
      const previousUserId = currentUser.current;
      currentUser.current = undefined;
      queryClient.clear();
      license.reset();
      clubSelection.detach();
      setState({ status: "signedOut", expired, previousUserId, explicit });
    },
    [clubSelection, queryClient, license],
  );

  const enter = useCallback(
    (me: Me) => {
      currentUser.current = me.user.id;
      clubSelection.attach(me.user.id);
      setState({ status: "signedIn", me });
    },
    [clubSelection],
  );

  // Restore a session on load, or after "try again".
  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      setState({ status: "loading" });
      if (!(await tokenStore.get())) return setState({ status: "signedOut", expired: false });
      try {
        enter(await api.auth.me(controller.signal));
      } catch (error) {
        if (controller.signal.aborted) return;
        // A 401 is already routed through sessionEvents; anything else is a connection or server problem.
        if (!isUnauthorized(error)) setState({ status: "error", error });
      }
    })();
    return () => controller.abort();
  }, [api, tokenStore, enter, attempt]);

  // The API client reports a rejected token: return to sign-in.
  useEffect(() => sessionEvents.onExpired(() => finishSignedOut(true)), [sessionEvents, finishSignedOut]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const result = await api.auth.login({ email, password, device: deviceLabel() });
      enter({ user: result.user, clubs: result.clubs });
    },
    [api, enter],
  );

  const completeMicrosoft = useCallback(async (code: string) => {
    const result = await api.auth.completeMicrosoft(code);
    const me = { user: result.user, clubs: result.clubs };
    enter(me);
    return { me, path: result.return_to };
  }, [api, enter]);

  const completeSiteKings = useCallback(async (code: string) => {
    const result = await api.auth.completeSiteKings(code);
    const me = { user: result.user, clubs: result.clubs };
    enter(me);
    return { me, path: result.return_to };
  }, [api, enter]);

  const signOut = useCallback(async () => {
    try {
      await api.auth.logout();
    } catch {
      /* the token is forgotten locally regardless */
    }
    finishSignedOut(false, true);
  }, [api, finishSignedOut]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  const refreshMe = useCallback(async () => {
    const me = await api.auth.me();
    setState((current) => (current.status === "signedIn" ? { status: "signedIn", me } : current));
  }, [api]);
  const value = useMemo(() => ({ state, signIn, completeMicrosoft, completeSiteKings, signOut, retry, refreshMe }), [state, signIn, completeMicrosoft, completeSiteKings, signOut, retry, refreshMe]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("AuthProvider is missing");
  return value;
}

export function useMe(): Me {
  const { state } = useAuth();
  if (state.status !== "signedIn") throw new Error("useMe needs a signed-in user");
  return state.me;
}
